import { useEffect, useRef } from 'react'

/**
 * Échap ferme la pop-in — et UNE touche ferme UN niveau.
 *
 * Chaque pop-in montée s'inscrit ici ; un unique écouteur `keydown` sur
 * `window` ne prévient que la plus HAUTE. Sans ce registre, deux pop-ins
 * superposées (le catalogue et son aperçu, la médiathèque et son détail)
 * écouteraient toutes les deux et se fermeraient d'un coup — le rédacteur perd
 * sa recherche en voulant fermer un aperçu.
 *
 * « La plus haute » se mesure au **rang de premier rendu**, pas à l'ordre des
 * effets : React exécute les effets de bas en haut, un enfant monté EN MÊME
 * TEMPS que son parent s'inscrirait donc avant lui et se ferait voler la
 * touche. Le rendu, lui, va toujours du parent vers l'enfant.
 *
 * Le rang et la fermeture passent par des refs : l'effet ne dépend que de
 * `enabled`, si bien qu'un simple changement d'état ne réordonne rien.
 *
 * Un composant interne qui a son propre usage d'Échap (une liste
 * d'autocomplétion ouverte, une édition en ligne) le garde en appelant
 * `e.stopPropagation()` dans son `onKeyDown` : l'écouteur de `window` est en
 * phase de remontée, il ne verra rien.
 *
 * @param {() => void} onClose — fermeture de CE niveau.
 * @param {boolean} [enabled=true] — faux → ce niveau ne prend pas la touche
 *   (pop-in non montée, fermeture verrouillée pendant un enregistrement…).
 */
const levels = new Set()
let nextRank = 0
let listening = false

const onWindowKeyDown = (e) => {
  if (e.key !== 'Escape') return
  let top = null
  for (const level of levels) if (!top || level.rank > top.rank) top = level
  if (!top) return
  e.stopPropagation()
  top.onClose.current()
}

export const useEscapeClose = (onClose, enabled = true) => {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Rang attribué au rendu où ce niveau PREND la touche, et gardé tant qu'il
  // la garde : une confirmation qui s'ouvre dans un bloc monté depuis
  // longtemps doit passer devant, pas hériter du rang de son bloc.
  const rankRef = useRef(null)
  if (enabled && rankRef.current === null) rankRef.current = nextRank++
  else if (!enabled) rankRef.current = null

  useEffect(() => {
    if (!enabled) return
    const level = { rank: rankRef.current, onClose: onCloseRef }
    levels.add(level)
    if (!listening) {
      window.addEventListener('keydown', onWindowKeyDown)
      listening = true
    }
    return () => {
      levels.delete(level)
      if (levels.size === 0 && listening) {
        window.removeEventListener('keydown', onWindowKeyDown)
        listening = false
      }
    }
  }, [enabled])
}
